import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
    type WheelEvent
} from "react";
import type { ScrollContextType, ScrollDirection } from "../../types/ScrollContextType";

// Max scroll speed in px/sec at full intensity (roughly matches the old 15px/frame @60fps).
// Speed is derived from real elapsed time (not a fixed per-frame step) so it stays smooth
// regardless of the display's refresh rate, and the loop keeps running across repeated
// startScrolling calls (mousemove/hover fire far more often than rAF) instead of being
// torn down and rebuilt every event, which was the main source of the stutter.
const MAX_SCROLL_SPEED = 900;

// Callers pass a 0..1 ratio for "how far past the edge threshold is the cursor".
// Mapping straight through to speed made the common case (just barely into the
// trigger zone) feel like scrolling wasn't happening at all, so floor it at
// MIN_INTENSITY - still ramps up smoothly, but is never imperceptibly slow.
const MIN_INTENSITY = 0.4;

export const ScrollContext = createContext<any>({});

export const ScrollProvider = ({ children }: PropsWithChildren) => {
    const headerContentRef = useRef<HTMLDivElement | null>(null);
    const headerContainerRef = useRef<HTMLDivElement | null>(null);
    const cornerHeaderRef = useRef<HTMLDivElement | null>(null);

    const dailyInfoRef = useRef<HTMLDivElement | null>(null);

    const columnsContentRef = useRef<HTMLDivElement | null>(null);
    const columnsContainerRef = useRef<HTMLDivElement | null>(null);

    const scheduleGridRef = useRef<HTMLDivElement | null>(null);

    // Body (which owns scheduleGridRef) unmounts/remounts as `loading` toggles, so the
    // ref's .current can go from populated -> null -> a new node without the ref object's
    // identity ever changing. Effects that depend on scheduleGridRef alone (useCellWidth,
    // the ResizeObserver below) would silently keep using whatever .current was at the time
    // they first ran. Mirroring the attached node into state lets those effects depend on
    // the actual node and re-run whenever it changes.
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const setScheduleGridRef = useCallback((node: HTMLDivElement | null) => {
        scheduleGridRef.current = node;
        setGridElement(node);
    }, []);

    // Same reasoning for the header pane: it is what useCellWidth measures while the body
    // is unmounted, and reading the ref's .current from an effect is a race against when
    // React attaches it. Mirroring the node into state makes that measurable reliably.
    const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(null);
    const setHeaderContainerRef = useCallback((node: HTMLDivElement | null) => {
        headerContainerRef.current = node;
        setHeaderElement(node);
    }, []);

    const scrollRequestRef = useRef<number | null>(null);
    const scrollDirectionRef = useRef<ScrollDirection | null>(null);
    const scrollIntensityRef = useRef<number>(1);
    const lastScrollTimeRef = useRef<number | null>(null);
    const [scrollbarWidth, setScrollbarWidth] = useState<number>(0);
    const [scrollbarHeight, setScrollbarHeight] = useState<number>(0);

    const stopScrolling = useCallback(() => {
        if (scrollRequestRef.current) {
            cancelAnimationFrame(scrollRequestRef.current);
            scrollRequestRef.current = null;
        }
        scrollDirectionRef.current = null;
        lastScrollTimeRef.current = null;
    }, []);

    const startScrolling = useCallback((direction: ScrollDirection, intensity: number = 1) => {
        const clamped = Math.min(1, Math.max(0, intensity));
        scrollDirectionRef.current = direction;
        scrollIntensityRef.current = clamped === 0 ? 0 : MIN_INTENSITY + (1 - MIN_INTENSITY) * clamped;

        if (scrollRequestRef.current) return;

        const scrollStep = (timestamp: number) => {
            const gridEl = scheduleGridRef.current;
            const currentDirection = scrollDirectionRef.current;
            if (!gridEl || !currentDirection) {
                stopScrolling();
                return;
            }
            // Some callers (e.g. drag-to-create) recreate their mousemove handler on every
            // event, which tears this loop down and restarts it constantly - so the first
            // frame after a (re)start can't be treated as zero elapsed time, or scroll
            // progress never accumulates. Assume one frame's worth (~16ms) instead.
            const last = lastScrollTimeRef.current;
            const deltaSeconds = last == null ? (1 / 60) : Math.min(timestamp - last, 100) / 1000;
            lastScrollTimeRef.current = timestamp;
            const scrollAmount = MAX_SCROLL_SPEED * scrollIntensityRef.current * deltaSeconds;

            switch (currentDirection) {
                case "up":
                    gridEl.scrollTop -= scrollAmount;
                    break;
                case "down":
                    gridEl.scrollTop += scrollAmount;
                    break;
                case "left":
                    gridEl.scrollLeft -= scrollAmount;
                    break;
                case "right":
                    gridEl.scrollLeft += scrollAmount;
                    break;
            }
            scrollRequestRef.current = requestAnimationFrame(scrollStep);
        };
        scrollRequestRef.current = requestAnimationFrame(scrollStep);
    }, [stopScrolling]);

    const handleGridScroll = useCallback(() => {
        // No grid (e.g. while loading) means the next one starts at 0,0
        const top = scheduleGridRef.current?.scrollTop ?? 0;
        const left = scheduleGridRef.current?.scrollLeft ?? 0;

        // Sync transforms
        if (headerContentRef.current) headerContentRef.current.style.transform = `translateX(-${left}px)`;
        if (columnsContentRef.current) columnsContentRef.current.style.transform = `translateY(-${top}px)`;

        // Sync scrolled classes directly on DOM nodes
        const isScrolledY = top > 0;
        const isScrolledX = left > 0;
        cornerHeaderRef.current?.classList.toggle("scrolled-y", isScrolledY);
        headerContainerRef.current?.classList.toggle("scrolled-y", isScrolledY);
        cornerHeaderRef.current?.classList.toggle("scrolled-x", isScrolledX);
        columnsContainerRef.current?.classList.toggle("scrolled-x", isScrolledX);
    }, []);

    const handlePaneWheel = useCallback((e: WheelEvent<HTMLDivElement>) => {
        if (!scheduleGridRef.current) {
            return;
        }

        // e.preventDefault();
        //horizonatal scrolling
        if (e.deltaX !== 0 || e.shiftKey) {
            scheduleGridRef.current.scrollLeft += e.deltaY + e.deltaX;
        } else {
            // Default vertical scrolling
            scheduleGridRef.current.scrollTop += e.deltaY;
        }
    }, []);

    // Body remounts on mode/filter changes and the new grid starts at 0,0 without firing a
    // scroll event, and content shrinking (e.g. grouping, fewer rows) can clamp the scroll
    // offset. Either way the header and resource column would keep the old offset, so resync.
    useEffect(() => {
        handleGridScroll();
        if (!gridElement) return;
        const resizeObserver = new ResizeObserver(() => handleGridScroll());
        resizeObserver.observe(gridElement);
        if (gridElement.firstElementChild) resizeObserver.observe(gridElement.firstElementChild);
        return () => resizeObserver.disconnect();
    }, [gridElement, handleGridScroll]);

    // Native wheel scrolling runs on the compositor thread: the grid moves a frame before its
    // scroll event reaches handleGridScroll, so the header and resource column (synced via
    // transforms) visibly lag behind it. Applying the wheel delta ourselves keeps the scroll
    // and the transforms in the same frame. Touch/keyboard scrolling still go the native way.
    useEffect(() => {
        if (!gridElement) return;
        const handleWheel = (e: globalThis.WheelEvent) => {
            if (e.ctrlKey) return; // pinch-zoom

            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? gridElement.clientHeight : 1;
            let dx = e.deltaX * unit;
            let dy = e.deltaY * unit;
            if (e.shiftKey && dx === 0) {
                dx = dy;
                dy = 0;
            }

            const { scrollLeft, scrollTop } = gridElement;
            gridElement.scrollLeft = scrollLeft + dx;
            gridElement.scrollTop = scrollTop + dy;

            // Nothing moved (already at the edge): let the page scroll instead
            if (gridElement.scrollLeft === scrollLeft && gridElement.scrollTop === scrollTop) return;

            e.preventDefault();
            handleGridScroll();
        };
        gridElement.addEventListener("wheel", handleWheel, { passive: false });
        return () => gridElement.removeEventListener("wheel", handleWheel);
    }, [gridElement, handleGridScroll]);

    useEffect(() => {
        if (!gridElement) return;
        // Deferred to the next frame: the state updates change layout, and doing that inside the
        // observer callback (e.g. when the action bar appears and shrinks the grid) triggers
        // "ResizeObserver loop" errors
        let frame = 0;
        const resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const hasVerticalScrollbar = gridElement.scrollHeight > gridElement.clientHeight;
                const hasHorizontalScrollbar = gridElement.scrollWidth > gridElement.clientWidth;
                setScrollbarWidth(hasVerticalScrollbar ? gridElement.offsetWidth - gridElement.clientWidth : 0);
                setScrollbarHeight(hasHorizontalScrollbar ? gridElement.offsetHeight - gridElement.clientHeight : 0);
            });
        });
        resizeObserver.observe(gridElement);
        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, [gridElement]);

    useEffect(() => {
        return () => stopScrolling();
    }, [stopScrolling]);

    const value: ScrollContextType = useMemo(() => ({
        headerContentRef,
        headerContainerRef,
        cornerHeaderRef,
        dailyInfoRef,
        columnsContentRef,
        columnsContainerRef,
        scheduleGridRef,
        setScheduleGridRef,
        setHeaderContainerRef,
        gridElement,
        headerElement,
        scrollbarWidth,
        scrollbarHeight,
        startScrolling,
        stopScrolling,
        handleGridScroll,
        handlePaneWheel
    }), [
        headerContentRef,
        headerContainerRef,
        cornerHeaderRef,
        dailyInfoRef,
        columnsContentRef,
        columnsContainerRef,
        scheduleGridRef,
        setScheduleGridRef,
        setHeaderContainerRef,
        gridElement,
        headerElement,
        scrollbarWidth,
        scrollbarHeight,
        startScrolling,
        stopScrolling,
        handleGridScroll,
        handlePaneWheel
    ]);

    return (
        <ScrollContext.Provider value={value}>
            {children}
        </ScrollContext.Provider>
    );
}
